/**
 * LifecycleAutomationPanel — Operations Console > Lifecycle Automation.
 *
 * Iteration 70.1.
 *
 * Surfaces the Mongo-backed deferred-send queue + the background worker
 * that fires nurture / trial / weekly digest emails. Platform-admin only.
 *
 *  GET    /api/comms/lifecycle/state
 *  GET    /api/comms/lifecycle/queue
 *  POST   /api/comms/lifecycle/pause
 *  POST   /api/comms/lifecycle/resume
 *  POST   /api/comms/lifecycle/run-once
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Activity, Pause, Play, RefreshCw, Loader2, Zap, Clock,
  CheckCircle2, XCircle, AlertTriangle, MailQuestion,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending:   { label: "Pending",   color: "#7dd3fc", icon: Clock },
  sent:      { label: "Sent",      color: "#86efac", icon: CheckCircle2 },
  failed:    { label: "Failed",    color: "#fca5a5", icon: XCircle },
  cancelled: { label: "Cancelled", color: "#cbd5e1", icon: MailQuestion },
  skipped:   { label: "Skipped",   color: "#fbbf24", icon: AlertTriangle },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};

export default function LifecycleAutomationPanel() {
  const [state, setState]   = useState(null);
  const [queue, setQueue]   = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        api.get("/comms/lifecycle/state"),
        api.get("/comms/lifecycle/queue", {
          params: statusFilter ? { status: statusFilter } : {},
        }),
      ]);
      setState(s.data);
      setQueue(q.data.queue || []);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to load lifecycle data");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const togglePause = async () => {
    setActing(true);
    try {
      const paused = state?.worker?.paused;
      await api.post(paused ? "/comms/lifecycle/resume" : "/comms/lifecycle/pause");
      toast.success(paused ? "Lifecycle worker resumed" : "Lifecycle worker paused");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setActing(false); }
  };

  const runOnce = async () => {
    setActing(true);
    try {
      const res = await api.post("/comms/lifecycle/run-once");
      const sum = res.data?.summary || {};
      toast.success(
        `Tick complete · processed ${sum.processed ?? 0} · sent ${sum.sent ?? 0} ` +
        `· cancelled ${sum.cancelled_pre_send ?? 0} · failed ${sum.failed ?? 0}`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setActing(false); }
  };

  const counts = state?.counts || {};
  const paused = !!state?.worker?.paused;
  const alive  = !!state?.worker?.alive;

  return (
    <div
      className="rounded-xl border bg-[var(--fop-surface)]"
      style={{ borderColor: "var(--fop-border)" }}
      data-testid="lifecycle-automation-panel"
    >
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{ background: "rgba(249,115,22,0.10)", border: "1px solid rgba(249,115,22,0.35)" }}>
            <Activity size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">Lifecycle Automation</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              Drip queue · weekly digests · idempotent · restart-safe
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                  style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}
                  data-testid="lifecycle-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={runOnce} disabled={acting || !alive}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs uppercase tracking-wider font-mono text-white"
                  style={{ background: "rgba(249,115,22,0.85)" }}
                  data-testid="lifecycle-run-now">
            <Zap size={13} /> Run tick now
          </button>
          <button onClick={togglePause} disabled={acting}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                  style={{ borderColor: "var(--fop-border)",
                            color: paused ? "#86efac" : "#fbbf24" }}
                  data-testid="lifecycle-pause-toggle">
            {paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
          </button>
        </div>
      </div>

      {/* Worker status banner */}
      <div className="px-5 py-3 border-b" style={{ borderColor: "var(--fop-border)" }}>
        <div className="rounded-md p-3 flex items-center gap-3 flex-wrap"
             style={{
               background: alive && !paused ? "rgba(34,197,94,0.07)" :
                           paused ? "rgba(245,158,11,0.07)" : "rgba(220,38,38,0.07)",
               border: `1px solid ${alive && !paused ? "rgba(34,197,94,0.45)" :
                           paused ? "rgba(245,158,11,0.45)" : "rgba(220,38,38,0.45)"}`,
             }}
             data-testid="lifecycle-status-banner">
          {alive && !paused ? (
            <CheckCircle2 size={16} className="text-[var(--fop-success)]" />
          ) : paused ? (
            <Pause size={16} style={{ color: "#fbbf24" }} />
          ) : (
            <XCircle size={16} style={{ color: "#fca5a5" }} />
          )}
          <div className="text-sm text-[var(--fop-text)]">
            <strong>
              {!alive ? "Worker not running" :
                paused ? "Worker paused" : "Worker running"}.
            </strong>{" "}
            <span className="text-[var(--fop-text-2)]">
              Tick every <span className="font-mono">{state?.worker?.tick_interval_sec ?? 60}s</span>.
              {state?.worker?.last_run_at && <> Last tick: <span className="font-mono">{fmt(state.worker.last_run_at)}</span>.</>}
              {state?.next_run && <> Next due: <span className="font-mono">{fmt(state.next_run.run_at)}</span>{state.next_run.template_code ? <> · <span className="font-mono">{state.next_run.template_code}</span></> : null}.</>}
            </span>
          </div>
        </div>
      </div>

      {/* Counts */}
      <div className="px-5 py-4 border-b grid grid-cols-2 sm:grid-cols-4 gap-2.5"
           style={{ borderColor: "var(--fop-border)" }}>
        {["pending", "sent", "cancelled", "failed"].map((k) => (
          <Chip key={k} label={k} value={counts[k] ?? 0}
                active={statusFilter === k}
                onClick={() => setStatusFilter(statusFilter === k ? "" : k)}
                color={STATUS_META[k]?.color}
                testid={`lifecycle-filter-${k}`} />
        ))}
      </div>

      {/* Queue */}
      <div data-testid="lifecycle-queue-list">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : queue.length === 0 ? (
          <div className="p-10 text-center text-[var(--fop-text-3)] text-sm" data-testid="lifecycle-queue-empty">
            No scheduled emails {statusFilter ? `with status "${statusFilter}"` : "yet"}.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--fop-border)" }}>
            {queue.map((row) => <QueueRow key={row.id} row={row} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ label, value, active, onClick, color, testid }) {
  return (
    <button onClick={onClick}
            className="rounded-md p-3 text-left border transition"
            style={{
              borderColor: active ? color || "var(--fop-primary)" : "var(--fop-border)",
              background: active ? `${color || "var(--fop-primary)"}1A` : "rgba(15,23,42,0.4)",
            }}
            data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-[0.18em]"
           style={{ color: color || "var(--fop-text-3)" }}>
        {label.replace(/_/g, " ")}
      </div>
      <div className="text-2xl font-semibold text-[var(--fop-text)] mt-1">{value}</div>
    </button>
  );
}

function QueueRow({ row }) {
  const meta = STATUS_META[row.status] || { label: row.status, color: "#9ca3af", icon: Clock };
  const Icon = meta.icon;
  return (
    <div className="px-5 py-3 flex items-start gap-3 flex-wrap" data-testid={`lifecycle-queue-row-${row.id}`}>
      <div className="w-7 h-7 rounded-md inline-flex items-center justify-center mt-0.5"
           style={{ background: `${meta.color}1A`, border: `1px solid ${meta.color}55` }}>
        <Icon size={14} style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[var(--fop-text)] font-medium truncate">
          {row.template_code}
          <span className="ml-2 text-[11px] font-mono uppercase tracking-[0.18em]"
                style={{ color: meta.color }}>
            {meta.label}
          </span>
        </div>
        <div className="text-xs text-[var(--fop-text-3)] font-mono mt-0.5 truncate">
          to {(row.to || []).join(", ") || "—"} · dedupe {row.dedupe_key}
        </div>
        {row.reason && (
          <div className="text-xs mt-1" style={{ color: meta.color }}>
            {row.reason}
          </div>
        )}
      </div>
      <div className="text-xs text-[var(--fop-text-3)] font-mono whitespace-nowrap">
        {fmt(row.run_at)}
      </div>
    </div>
  );
}
